/**
 * Subscriptions domain events — catalog 1.17.0 additions (commerce-platform
 * Phase 7 / plan P3-T6 "catalog v11").
 *
 * NOTE (catalog release naming, mirrors the R-5 drift note in catalog/email.ts
 * and the media.ts header): the plan's "v11" is plan fiction. The latest real
 * catalog release before this file was 1.16.0 (`media:*`), so these
 * definitions land in the next real release, `since: '1.17.0'`.
 *
 * Canonical single truth (plan §3 drift note): the draft alias
 * `subscriptions:subscription-suspended` reserved in registry.ts is
 * SUPERSEDED — the dunning state machine (services/subscriptions dunning-
 * machine.ts) emits the bare `subscriptions:suspended`, and that is the
 * published catalog name (RESERVED_LEGACY_NAME_MAP reconciled accordingly).
 *
 * Transport: all eleven names ride the durable bus (ClusterEventPublisher via
 * @cluster/events — Mongo durable record + Redis fan-out), source
 * `subscriptions`, fanned out to the existing org-scoped
 * `ROOM_COMMERCE_UPDATES` room (no new room).
 *
 * PII: payloads carry ids and dates only — never names, emails, or card data.
 */
import {
  asEventRecord,
  defineEvent,
  optionalString,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

/** Basis-point / count integer gate (local, mirrors the media percent check style). */
function requireInt(value: unknown, field: string, context: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new Error(`${context}: field "${field}" must be an integer`);
  }
  return value;
}

function optionalInt(value: unknown, field: string, context: string): number | undefined {
  if (value === undefined || value === null) return undefined;
  return requireInt(value, field, context);
}

// ---------------------------------------------------------------------------
// Payload types
// ---------------------------------------------------------------------------

export interface SubscriptionsSubscribedPayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  planId: string;
  /** Row status at creation: 'active' or 'trial' (P7-A2 auto-enrollment). */
  status: string;
  startedAt: string;
  trialEndsAt?: string;
}

export interface SubscriptionsActivatedPayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  planId?: string;
  from: string;
  to: string;
  activatedAt: string;
}

export interface SubscriptionsBillingSucceededPayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  planId: string;
  /** ISO instant of the `currentPeriodEnd` that was billed. */
  periodKey: string;
  succeededAt: string;
  attempt?: number;
  orderId?: string;
}

export interface SubscriptionsBillingFailedPayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  from: string;
  to: string;
  occurredAt: string;
  attempt?: number;
  reason?: string;
  nextRetryAt?: string;
}

export interface SubscriptionsPastDuePayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  from: string;
  to: string;
  occurredAt: string;
  attempt?: number;
  nextRetryAt?: string;
}

export interface SubscriptionsGracePayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  from: string;
  to: string;
  occurredAt: string;
  graceEndsAt?: string;
  cancelDueAt?: string;
}

export interface SubscriptionsSuspendedPayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  from: string;
  to: string;
  occurredAt: string;
  cancelDueAt?: string;
}

export interface SubscriptionsRestoredPayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  from: string;
  to: string;
  occurredAt: string;
  attempt?: number;
  restoreReason?: string;
}

export interface SubscriptionsCancelledPayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  from: string;
  to: string;
  occurredAt: string;
  reason?: string;
}

export interface SubscriptionsExpiredPayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  planId?: string;
  from: string;
  to: string;
  expiredAt: string;
}

export interface SubscriptionsEntitlementLimitHitPayload extends Record<string, unknown> {
  orgId: string;
  subscriptionId: string;
  customerId: string;
  resourceType: string;
  /** Limit window the bucket was keyed to (per the entitlement engine contract). */
  per: string;
  max: number;
  used: number;
}

// ---------------------------------------------------------------------------
// Definitions (media.ts style: TYPE const -> schema -> defineEvent -> record)
// ---------------------------------------------------------------------------

const SUBSCRIBED_TYPE = 'subscriptions:subscribed';
const subscribedSchema: EventSchema<SubscriptionsSubscribedPayload> = {
  parse(data) {
    const record = asEventRecord(data, SUBSCRIBED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', SUBSCRIBED_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', SUBSCRIBED_TYPE),
      customerId: requireString(record.customerId, 'customerId', SUBSCRIBED_TYPE),
      planId: requireString(record.planId, 'planId', SUBSCRIBED_TYPE),
      status: requireString(record.status, 'status', SUBSCRIBED_TYPE),
      startedAt: requireString(record.startedAt, 'startedAt', SUBSCRIBED_TYPE),
      trialEndsAt: optionalString(record.trialEndsAt, 'trialEndsAt', SUBSCRIBED_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_SUBSCRIBED_EVENT = defineEvent<SubscriptionsSubscribedPayload>({
  type: SUBSCRIBED_TYPE,
  version: 1,
  schema: subscribedSchema,
  auditable: true,
  description:
    'A customer was subscribed to a plan (trial auto-enrollment or direct active start; ids only).',
  since: '1.17.0',
});

const ACTIVATED_TYPE = 'subscriptions:activated';
const activatedSchema: EventSchema<SubscriptionsActivatedPayload> = {
  parse(data) {
    const record = asEventRecord(data, ACTIVATED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', ACTIVATED_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', ACTIVATED_TYPE),
      customerId: requireString(record.customerId, 'customerId', ACTIVATED_TYPE),
      planId: optionalString(record.planId, 'planId', ACTIVATED_TYPE),
      from: requireString(record.from, 'from', ACTIVATED_TYPE),
      to: requireString(record.to, 'to', ACTIVATED_TYPE),
      activatedAt: requireString(record.activatedAt, 'activatedAt', ACTIVATED_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_ACTIVATED_EVENT = defineEvent<SubscriptionsActivatedPayload>({
  type: ACTIVATED_TYPE,
  version: 1,
  schema: activatedSchema,
  auditable: true,
  description: 'A subscription moved to active through the lifecycle activate action.',
  since: '1.17.0',
});

const BILLING_SUCCEEDED_TYPE = 'subscriptions:billing-succeeded';
const billingSucceededSchema: EventSchema<SubscriptionsBillingSucceededPayload> = {
  parse(data) {
    const record = asEventRecord(data, BILLING_SUCCEEDED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', BILLING_SUCCEEDED_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', BILLING_SUCCEEDED_TYPE),
      customerId: requireString(record.customerId, 'customerId', BILLING_SUCCEEDED_TYPE),
      planId: requireString(record.planId, 'planId', BILLING_SUCCEEDED_TYPE),
      periodKey: requireString(record.periodKey, 'periodKey', BILLING_SUCCEEDED_TYPE),
      succeededAt: requireString(record.succeededAt, 'succeededAt', BILLING_SUCCEEDED_TYPE),
      attempt: optionalInt(record.attempt, 'attempt', BILLING_SUCCEEDED_TYPE),
      orderId: optionalString(record.orderId, 'orderId', BILLING_SUCCEEDED_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_BILLING_SUCCEEDED_EVENT =
  defineEvent<SubscriptionsBillingSucceededPayload>({
    type: BILLING_SUCCEEDED_TYPE,
    version: 1,
    schema: billingSucceededSchema,
    auditable: true,
    description:
      'A recurring charge succeeded and the billing period rolled (order reference by id only).',
    since: '1.17.0',
  });

const BILLING_FAILED_TYPE = 'subscriptions:billing-failed';
const billingFailedSchema: EventSchema<SubscriptionsBillingFailedPayload> = {
  parse(data) {
    const record = asEventRecord(data, BILLING_FAILED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', BILLING_FAILED_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', BILLING_FAILED_TYPE),
      customerId: requireString(record.customerId, 'customerId', BILLING_FAILED_TYPE),
      from: requireString(record.from, 'from', BILLING_FAILED_TYPE),
      to: requireString(record.to, 'to', BILLING_FAILED_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', BILLING_FAILED_TYPE),
      attempt: optionalInt(record.attempt, 'attempt', BILLING_FAILED_TYPE),
      reason: optionalString(record.reason, 'reason', BILLING_FAILED_TYPE),
      nextRetryAt: optionalString(record.nextRetryAt, 'nextRetryAt', BILLING_FAILED_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_BILLING_FAILED_EVENT = defineEvent<SubscriptionsBillingFailedPayload>({
  type: BILLING_FAILED_TYPE,
  version: 1,
  schema: billingFailedSchema,
  auditable: true,
  description:
    'A charge attempt failed inside the dunning cycle (retry beat or entry into grace; reason string only).',
  since: '1.17.0',
});

const PAST_DUE_TYPE = 'subscriptions:past-due';
const pastDueSchema: EventSchema<SubscriptionsPastDuePayload> = {
  parse(data) {
    const record = asEventRecord(data, PAST_DUE_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', PAST_DUE_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', PAST_DUE_TYPE),
      customerId: requireString(record.customerId, 'customerId', PAST_DUE_TYPE),
      from: requireString(record.from, 'from', PAST_DUE_TYPE),
      to: requireString(record.to, 'to', PAST_DUE_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', PAST_DUE_TYPE),
      attempt: optionalInt(record.attempt, 'attempt', PAST_DUE_TYPE),
      nextRetryAt: optionalString(record.nextRetryAt, 'nextRetryAt', PAST_DUE_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_PAST_DUE_EVENT = defineEvent<SubscriptionsPastDuePayload>({
  type: PAST_DUE_TYPE,
  version: 1,
  schema: pastDueSchema,
  auditable: true,
  description: 'The first failed charge of a dunning cycle moved the subscription to past_due.',
  since: '1.17.0',
});

const GRACE_TYPE = 'subscriptions:grace';
const graceSchema: EventSchema<SubscriptionsGracePayload> = {
  parse(data) {
    const record = asEventRecord(data, GRACE_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', GRACE_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', GRACE_TYPE),
      customerId: requireString(record.customerId, 'customerId', GRACE_TYPE),
      from: requireString(record.from, 'from', GRACE_TYPE),
      to: requireString(record.to, 'to', GRACE_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', GRACE_TYPE),
      graceEndsAt: optionalString(record.graceEndsAt, 'graceEndsAt', GRACE_TYPE),
      cancelDueAt: optionalString(record.cancelDueAt, 'cancelDueAt', GRACE_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_GRACE_EVENT = defineEvent<SubscriptionsGracePayload>({
  type: GRACE_TYPE,
  version: 1,
  schema: graceSchema,
  auditable: true,
  description:
    'The retry schedule was exhausted and the subscription entered the grace window (fires alongside the final billing-failed beat).',
  since: '1.17.0',
});

const SUSPENDED_TYPE = 'subscriptions:suspended';
const suspendedSchema: EventSchema<SubscriptionsSuspendedPayload> = {
  parse(data) {
    const record = asEventRecord(data, SUSPENDED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', SUSPENDED_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', SUSPENDED_TYPE),
      customerId: requireString(record.customerId, 'customerId', SUSPENDED_TYPE),
      from: requireString(record.from, 'from', SUSPENDED_TYPE),
      to: requireString(record.to, 'to', SUSPENDED_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', SUSPENDED_TYPE),
      cancelDueAt: optionalString(record.cancelDueAt, 'cancelDueAt', SUSPENDED_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_SUSPENDED_EVENT = defineEvent<SubscriptionsSuspendedPayload>({
  type: SUSPENDED_TYPE,
  version: 1,
  schema: suspendedSchema,
  auditable: true,
  description:
    'The grace window expired and the subscription was suspended (canonical name; supersedes the draft subscriptions:subscription-suspended alias).',
  since: '1.17.0',
});

const RESTORED_TYPE = 'subscriptions:restored';
const restoredSchema: EventSchema<SubscriptionsRestoredPayload> = {
  parse(data) {
    const record = asEventRecord(data, RESTORED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', RESTORED_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', RESTORED_TYPE),
      customerId: requireString(record.customerId, 'customerId', RESTORED_TYPE),
      from: requireString(record.from, 'from', RESTORED_TYPE),
      to: requireString(record.to, 'to', RESTORED_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', RESTORED_TYPE),
      attempt: optionalInt(record.attempt, 'attempt', RESTORED_TYPE),
      restoreReason: optionalString(record.restoreReason, 'restoreReason', RESTORED_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_RESTORED_EVENT = defineEvent<SubscriptionsRestoredPayload>({
  type: RESTORED_TYPE,
  version: 1,
  schema: restoredSchema,
  auditable: true,
  description: 'Restore-on-payment: a successful charge returned the subscription to active.',
  since: '1.17.0',
});

const CANCELLED_TYPE = 'subscriptions:cancelled';
const cancelledSchema: EventSchema<SubscriptionsCancelledPayload> = {
  parse(data) {
    const record = asEventRecord(data, CANCELLED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', CANCELLED_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', CANCELLED_TYPE),
      customerId: requireString(record.customerId, 'customerId', CANCELLED_TYPE),
      from: requireString(record.from, 'from', CANCELLED_TYPE),
      to: requireString(record.to, 'to', CANCELLED_TYPE),
      occurredAt: requireString(record.occurredAt, 'occurredAt', CANCELLED_TYPE),
      reason: optionalString(record.reason, 'reason', CANCELLED_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_CANCELLED_EVENT = defineEvent<SubscriptionsCancelledPayload>({
  type: CANCELLED_TYPE,
  version: 1,
  schema: cancelledSchema,
  auditable: true,
  description: 'The subscription reached the terminal cancelled state (dunning expiry or user cancel).',
  since: '1.17.0',
});

const EXPIRED_TYPE = 'subscriptions:expired';
const expiredSchema: EventSchema<SubscriptionsExpiredPayload> = {
  parse(data) {
    const record = asEventRecord(data, EXPIRED_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', EXPIRED_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', EXPIRED_TYPE),
      customerId: requireString(record.customerId, 'customerId', EXPIRED_TYPE),
      planId: optionalString(record.planId, 'planId', EXPIRED_TYPE),
      from: requireString(record.from, 'from', EXPIRED_TYPE),
      to: requireString(record.to, 'to', EXPIRED_TYPE),
      expiredAt: requireString(record.expiredAt, 'expiredAt', EXPIRED_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_EXPIRED_EVENT = defineEvent<SubscriptionsExpiredPayload>({
  type: EXPIRED_TYPE,
  version: 1,
  schema: expiredSchema,
  auditable: true,
  description:
    'Boundary expiry: cancel-at-period-end or autoRenew=false rows expire at the period boundary instead of being charged.',
  since: '1.17.0',
});

const ENTITLEMENT_LIMIT_HIT_TYPE = 'subscriptions:entitlement-limit-hit';
const entitlementLimitHitSchema: EventSchema<SubscriptionsEntitlementLimitHitPayload> = {
  parse(data) {
    const record = asEventRecord(data, ENTITLEMENT_LIMIT_HIT_TYPE);
    return {
      orgId: requireString(record.orgId, 'orgId', ENTITLEMENT_LIMIT_HIT_TYPE),
      subscriptionId: requireString(record.subscriptionId, 'subscriptionId', ENTITLEMENT_LIMIT_HIT_TYPE),
      customerId: requireString(record.customerId, 'customerId', ENTITLEMENT_LIMIT_HIT_TYPE),
      resourceType: requireString(record.resourceType, 'resourceType', ENTITLEMENT_LIMIT_HIT_TYPE),
      per: requireString(record.per, 'per', ENTITLEMENT_LIMIT_HIT_TYPE),
      max: requireInt(record.max, 'max', ENTITLEMENT_LIMIT_HIT_TYPE),
      used: requireInt(record.used, 'used', ENTITLEMENT_LIMIT_HIT_TYPE),
    };
  },
};

export const SUBSCRIPTIONS_ENTITLEMENT_LIMIT_HIT_EVENT =
  defineEvent<SubscriptionsEntitlementLimitHitPayload>({
    type: ENTITLEMENT_LIMIT_HIT_TYPE,
    version: 1,
    schema: entitlementLimitHitSchema,
    auditable: true,
    description:
      'An entitlement consume was blocked by a plan limit (boundary denial; ids, window, and counts only).',
    since: '1.17.0',
  });

export const SUBSCRIPTIONS_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [SUBSCRIPTIONS_SUBSCRIBED_EVENT.type]: SUBSCRIPTIONS_SUBSCRIBED_EVENT,
  [SUBSCRIPTIONS_ACTIVATED_EVENT.type]: SUBSCRIPTIONS_ACTIVATED_EVENT,
  [SUBSCRIPTIONS_BILLING_SUCCEEDED_EVENT.type]: SUBSCRIPTIONS_BILLING_SUCCEEDED_EVENT,
  [SUBSCRIPTIONS_BILLING_FAILED_EVENT.type]: SUBSCRIPTIONS_BILLING_FAILED_EVENT,
  [SUBSCRIPTIONS_PAST_DUE_EVENT.type]: SUBSCRIPTIONS_PAST_DUE_EVENT,
  [SUBSCRIPTIONS_GRACE_EVENT.type]: SUBSCRIPTIONS_GRACE_EVENT,
  [SUBSCRIPTIONS_SUSPENDED_EVENT.type]: SUBSCRIPTIONS_SUSPENDED_EVENT,
  [SUBSCRIPTIONS_RESTORED_EVENT.type]: SUBSCRIPTIONS_RESTORED_EVENT,
  [SUBSCRIPTIONS_CANCELLED_EVENT.type]: SUBSCRIPTIONS_CANCELLED_EVENT,
  [SUBSCRIPTIONS_EXPIRED_EVENT.type]: SUBSCRIPTIONS_EXPIRED_EVENT,
  [SUBSCRIPTIONS_ENTITLEMENT_LIMIT_HIT_EVENT.type]: SUBSCRIPTIONS_ENTITLEMENT_LIMIT_HIT_EVENT,
};
